import { getSupabase } from '@/lib/supabase';
import type { GameDefinition } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import type { RealtimeSubscription } from '../realtime';
import { decodeRowToGameDefinition, type GameDefinitionRow } from '../sync/codec';

export type GameDefinitionChangeType = 'INSERT' | 'UPDATE' | 'DELETE';

export interface GameDefinitionChange {
  type: GameDefinitionChangeType;
  definition: GameDefinition | null; // null for DELETE
}

export async function subscribeToGameDefinitions(
  userId: UserId,
  onChange: (change: GameDefinitionChange) => void,
): Promise<RealtimeSubscription> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to subscribe to game definitions');
  }

  const channel = client.channel(`game-definitions:${userId}`);

  // TypeScript has issues with multiple .on() calls, so we use type assertion
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment -- Supabase types don't properly support chained .on() calls
  const typedChannel = channel as any;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'INSERT',
      schema: 'public',
      table: 'game_definitions',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { new: GameDefinitionRow }) => {
      try {
        const definition = decodeRowToGameDefinition(payload.new);

        onChange({ type: 'INSERT', definition });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime game definition (INSERT)', err);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'UPDATE',
      schema: 'public',
      table: 'game_definitions',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { new: GameDefinitionRow }) => {
      try {
        const definition = decodeRowToGameDefinition(payload.new);

        onChange({ type: 'UPDATE', definition });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime game definition (UPDATE)', err);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Supabase types don't properly support chained .on() calls
  typedChannel.on(
    'postgres_changes',
    {
      event: 'DELETE',
      schema: 'public',
      table: 'game_definitions',
      filter: `user_id=eq.${userId}`,
    },
    (payload: { old: GameDefinitionRow }) => {
      try {
        const definition = decodeRowToGameDefinition(payload.old);

        onChange({ type: 'DELETE', definition });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime game definition (DELETE)', err);
      }
    },
  );

  channel.subscribe();
  return {
    unsubscribe: () => {
      void client.removeChannel(channel);
    },
  };
}
